import React, { useState, useEffect } from 'react';
import Button from '../../../components/Button';
import { ArrowLeft, Camera, Check } from 'lucide-react';
import { useAudience } from '../../../context/AudienceContext';
import ParsedQuestionsPreview from '../../../components/survey/ParsedQuestionsPreview';
import QuestionImageUpload from '../../../components/survey/QuestionImageUpload';
import InlineQuestionPreview from '../../../components/survey/InlineQuestionPreview';
import useQuestionParsingSocket from '../../../hooks/useQuestionParsingSocket';
import { createSimulation, handleApiError } from '../../../utils/simulationApi';

interface SegmentPersonaFilters {
  industryL1: string[];
  industryL2: string[];
  functions: string[];
  roles: string[];
}

interface InsightsFormProps {
  onSubmit: (simulationId: number) => void;
  selectedSegmentIds: number[];
  personaFilters: Record<number, SegmentPersonaFilters>;
  onBack: () => void;
  onEditStep?: () => void;
  initialFormData?: Record<string, any>;
  onFormDataChange?: (data: Record<string, any>) => void;
}

const SurveyAndFocusGroups: React.FC<InsightsFormProps> = ({ 
  onSubmit, 
  selectedSegmentIds, 
  personaFilters, 
  onBack,
  onEditStep,
  initialFormData = {},
  onFormDataChange = () => {}
}) => {
  const { audienceData } = useAudience();
  
  // Initialize form state from initialFormData
  const [simName, setSimName] = useState(initialFormData.simName || '');
  const [goal, setGoal] = useState(initialFormData.goal || '');
  const [questionsText, setQuestionsText] = useState(initialFormData.questionsText || '');
  const [context, setContext] = useState(initialFormData.context || '');
  const [useInternalDebate, setUseInternalDebate] = useState(initialFormData.useInternalDebate || false);
  const [enableAdaptiveContext, setEnableAdaptiveContext] = useState(initialFormData.enableAdaptiveContext || false);
  
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Question parsing state
  const [showParsedPreview, setShowParsedPreview] = useState(false);
  const [parsedQuestions, setParsedQuestions] = useState<any[]>(
    initialFormData.parsedQuestions || []
  );
  const [useParsedQuestions, setUseParsedQuestions] = useState(false);
  
  // Image upload state - supports multiple images per question
  const [questionImages, setQuestionImages] = useState<Map<number, Array<{path: string, url: string, file: File}>>>(new Map());
  const [questionLines, setQuestionLines] = useState<string[]>([]);
  
  // Store previous question lines for content-based image migration
  const prevQuestionLinesRef = React.useRef<string[]>([]);
  
  // Helper: Calculate similarity between two strings (0-1)
  const calculateSimilarity = (str1: string, str2: string): number => {
    const s1 = str1.toLowerCase().trim();
    const s2 = str2.toLowerCase().trim();
    if (s1 === s2) return 1;
    if (s1.length === 0 || s2.length === 0) return 0;
    
    const longer = s1.length > s2.length ? s1 : s2;
    const shorter = s1.length > s2.length ? s2 : s1;
    
    if (shorter.length / longer.length < 0.5) return shorter.length / longer.length * 0.5;
    
    let matches = 0;
    const shorterChars = shorter.split('');
    const longerChars = longer.split('');
    shorterChars.forEach((char, i) => {
      if (longerChars[i] === char) matches++;
    });
    
    return matches / longer.length;
  };
  
  // Helper: Find best matching old line for a new line
  const findBestMatch = (
    newLine: string, 
    oldLines: string[], 
    usedIndices: Set<number>
  ): { index: number; similarity: number } | null => {
    let bestMatch: { index: number; similarity: number } | null = null;
    
    oldLines.forEach((oldLine, oldIndex) => {
      if (usedIndices.has(oldIndex)) return;
      
      const similarity = calculateSimilarity(newLine, oldLine);
      if (similarity > 0.7 && (!bestMatch || similarity > bestMatch.similarity)) {
        bestMatch = { index: oldIndex, similarity };
      }
    });
    
    return bestMatch;
  };
  
  // Memoized callbacks for question parsing
  const handleParsingCompleted = React.useCallback((data: any) => {
    setParsedQuestions(data.parsed_questions || []);
    setShowParsedPreview(true);
  }, []);

  const handleParsingFailed = React.useCallback((data: any) => {
    console.error('Question parsing failed:', data);
    setError(data.error || data.detail || 'Failed to parse questions');
  }, []);

  // Question parsing socket hook
  const {
    parsingState,
    triggerParsing,
    clearParsingState,
    connect
  } = useQuestionParsingSocket({
    onParsingCompleted: handleParsingCompleted,
    onParsingFailed: handleParsingFailed,
    autoConnect: false
  });
  
  // Update parent component with form data changes
  useEffect(() => {
    const formData = {
      simName,
      goal,
      questionsText,
      context,
      useInternalDebate,
      enableAdaptiveContext
    };
    onFormDataChange(formData);
  }, [simName, goal, questionsText, context, useInternalDebate, enableAdaptiveContext]); // Remove onFormDataChange from dependencies

  
  // Split questions by newline + Q and migrate images based on content matching
  useEffect(() => {
    if (!useParsedQuestions) {
      const newLines = questionsText
        .split(/\n(?=Q)/i)  // Split on newline followed by Q (case-insensitive)
        .map((q: string) => q.trim())
        .filter((q: string) => q !== '');
      
      const oldLines = prevQuestionLinesRef.current;
      
      // Migrate images based on content matching
      setQuestionImages(prevImages => {
        if (oldLines.length === 0 || prevImages.size === 0) {
          return prevImages;
        }
        
        const newImagesMap = new Map<number, Array<{path: string, url: string, file: File}>>();
        const usedOldIndices = new Set<number>();
        
        newLines.forEach((newLine: string, newIndex: number) => {
          // First, try exact position match if content is similar
          if (newIndex < oldLines.length) {
            const samePosSimilarity = calculateSimilarity(newLine, oldLines[newIndex]);
            if (samePosSimilarity > 0.5 && !usedOldIndices.has(newIndex)) {
              const images = prevImages.get(newIndex);
              if (images && images.length > 0) {
                newImagesMap.set(newIndex, images);
                usedOldIndices.add(newIndex);
                return;
              }
            }
          }
          
          // Try to find best content match from other positions
          const match = findBestMatch(newLine, oldLines, usedOldIndices);
          if (match) {
            const images = prevImages.get(match.index);
            if (images && images.length > 0) {
              newImagesMap.set(newIndex, images);
              usedOldIndices.add(match.index);
            }
          }
        });
        
        return newImagesMap;
      });
      
      prevQuestionLinesRef.current = newLines;
      setQuestionLines(newLines);
    }
  }, [questionsText, useParsedQuestions]);
  
  // Handle field changes and notify parent about edits
  const handleFieldChange = (
    setter: React.Dispatch<React.SetStateAction<string>>, 
    value: string
  ) => {
    if (setter === setQuestionsText && useParsedQuestions) {
      return; // Block manual editing after AI parsing
    }
    
    setter(value);
    
    if (onEditStep) {
      onEditStep();
    }
  };

  // Initialize question images from parsed questions (for edit & rerun)
  useEffect(() => {
    if (initialFormData.parsedQuestions && Array.isArray(initialFormData.parsedQuestions)) {
      const imageMap = new Map<number, Array<{path: string, url: string, file: File}>>();
      
      initialFormData.parsedQuestions.forEach((question: any, index: number) => {
        if (question.image_paths && Array.isArray(question.image_paths) && question.image_paths.length > 0) {
          const images = question.image_paths.map((imagePath: string, imgIdx: number) => {
            let pathForComponent = imagePath;
            if (imagePath.startsWith('http')) {
              try {
                const url = new URL(imagePath);
                pathForComponent = url.pathname;
              } catch {
                pathForComponent = imagePath;
              }
            }
            
            return {
              path: imagePath,
              url: pathForComponent,
              file: new File([], `existing-image-${index}-${imgIdx}`, { type: 'image/jpeg' })
            };
          });
          imageMap.set(index, images);
        }
      });
      
      if (imageMap.size > 0) {
        setQuestionImages(imageMap);
      }
      
      if (initialFormData.questionsText) {
        const initialLines = initialFormData.questionsText
          .split(/\n(?=Q)/i)  // Split on newline followed by Q (case-insensitive)
          .map((q: string) => q.trim())
          .filter((q: string) => q !== '');
        prevQuestionLinesRef.current = initialLines;
      }
    }
  }, []);
  
  // Image handlers
  const handleImageUpload = (questionIndex: number, imageData: {path: string, url: string, file: File}) => {
    setQuestionImages(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(questionIndex) || [];
      newMap.set(questionIndex, [...existing, imageData]);
      return newMap;
    });
  };

  const handleImageRemove = (questionIndex: number, imageIndex?: number) => {
    setQuestionImages(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(questionIndex);
      
      if (!existing) return newMap;
      
      if (imageIndex !== undefined) {
        const updated = existing.filter((_, idx) => idx !== imageIndex);
        if (updated.length > 0) {
          newMap.set(questionIndex, updated);
        } else {
          newMap.delete(questionIndex);
        }
      } else {
        newMap.delete(questionIndex);
      }
      
      return newMap;
    });
  };
  
  // Form validation
  const isFormValid = questionsText.trim() !== '' && selectedSegmentIds.length > 0;
  const canParseQuestions = questionsText.trim() !== '' && !parsingState.isParsing;
  
  // Count total images
  const totalImageCount = Array.from(questionImages.values()).reduce((sum, imgs) => sum + imgs.length, 0);
  const questionsWithImages = questionImages.size;
  
  // Handle question parsing
  const handleParseQuestions = async () => {
    if (!canParseQuestions) return;
    
    try {
      clearParsingState();
      setError(null);
      setShowParsedPreview(false);
      
      if (!parsingState.isConnected) {
        await connect();
      }
      

      const questions = questionsText
        .split(/\n\s*\n/)
        .map((q: string) => q.trim())
        .filter((q: string) => q !== '');
      
      if (questions.length === 0) {
        setError('Please enter at least one question');
        return;
      }
      
      // Convert questionImages Map to Record<number, string[]> with paths
      const imagePaths: Record<number, string[]> = {};
      questionImages.forEach((imageDataArray, index) => {
        imagePaths[index] = imageDataArray.map(img => img.path);
      });
      
      await triggerParsing(questions, goal, context, Object.keys(imagePaths).length > 0 ? imagePaths : undefined);
      
    } catch (err) {
      console.error('Error triggering question parsing:', err);
      setError(err instanceof Error ? err.message : 'Failed to parse questions');
    }
  };
  
  // Handle using parsed questions
  const handleUseParsedQuestions = (questions: any[]) => {
    setParsedQuestions(questions);
    setUseParsedQuestions(true);
    setShowParsedPreview(false);
  };
  
  // Handle going back to question editing
  const handleBackToQuestions = () => {
    setShowParsedPreview(false);
    setUseParsedQuestions(false);
    setParsedQuestions([]);
    clearParsingState();
  };

  // Handle editing parsed questions
  const handleEditParsedQuestions = (_index?: number) => {
    setShowParsedPreview(true);
  };

  // Handle direct simulation start from preview
  const handleStartSimulation = async (questions: any[]) => {
    const simulationData = {
      audience_id: audienceData.audienceId,
      segment_ids: selectedSegmentIds,
      name: simName,
      persona_filters: personaFilters,
      task: 'survey_and_focus_groups',
      questions: questions,
      context: context,
      goal: goal, // Add goal to simulation data
      use_parsed_questions: true, // Flag to indicate pre-parsed questions
      use_internal_debate: useInternalDebate, // Internal debate toggle
      enable_adaptive_persona_context: enableAdaptiveContext // Context optimization toggle
    };
    
    const data = await createSimulation(simulationData);
    onSubmit(data.simulation_id);
  };

  // Handle submit
  const handleSubmit = async () => {
    if (!isFormValid || isSubmitting) return;
    
    if (!useParsedQuestions) {
      await handleParseQuestions();
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const questions = parsedQuestions;
      
      const simulationData = {
        audience_id: audienceData.audienceId,
        segment_ids: selectedSegmentIds,
        name: simName,
        persona_filters: personaFilters,
        task: 'survey_and_focus_groups',
        questions: questions,
        context: context,
        goal: goal, // Add goal to simulation data
        use_parsed_questions: useParsedQuestions, // Flag to indicate pre-parsed questions
        use_internal_debate: useInternalDebate, // Internal debate toggle
        enable_adaptive_persona_context: enableAdaptiveContext // Context optimization toggle
      };
      
      const data = await createSimulation(simulationData);
      onSubmit(data.simulation_id);
      
    } catch (err) {
      console.error('Error starting simulation:', err);
      handleApiError(err, setError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            icon={<ArrowLeft className="w-4 h-4 mr-1" />}
          >
            Back to use case selection
          </Button>
          <span className="text-sm text-gray-500">{selectedSegmentIds.length} segments selected</span>
        </div>
        
        {error ? (
          <div className="text-sm text-red-600 mb-3">
            {error}
          </div>
        ) : null}
      </div>

      {/* Show parsed questions preview or form */}
      {showParsedPreview ? (
        <ParsedQuestionsPreview
          parsedQuestions={parsedQuestions}
          onQuestionsUpdated={setParsedQuestions}
          onUseQuestions={handleUseParsedQuestions}
          onStartSimulation={handleStartSimulation}
          onGoBack={handleBackToQuestions}
          isLoading={parsingState.isParsing}
        />
      ) : (
        <>
          <div className="mb-6">
            <label htmlFor="simName" className="block text-sm font-medium text-gray-700 mb-2">
              Simulation Name
            </label>
            <input
              id="simName"
              type="text"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              value={simName}
              onChange={(e) => handleFieldChange(setSimName, e.target.value)}
              placeholder="Enter a name for your simulation"
            />
          </div>
          
          <div className="mb-6">
            <label htmlFor="goal" className="block text-sm font-medium text-gray-700 mb-2">
              Goal
            </label>
            <input
              id="goal"
              type="text"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              value={goal}
              onChange={(e) => handleFieldChange(setGoal, e.target.value)}
              placeholder="What do you want to achieve with this simulation?"
            />
          </div>
          
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="questionsText" className="block text-sm font-medium text-gray-700">
                Survey or Focus Group Questions
              </label>
              {totalImageCount > 0 && (
                <span className="flex items-center text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                  <Camera className="w-3 h-3 mr-1" />
                  {totalImageCount} {totalImageCount === 1 ? 'image' : 'images'} across {questionsWithImages} {questionsWithImages === 1 ? 'question' : 'questions'}
                </span>
              )}
            </div>
            <textarea
              id="questionsText"
              className={`block w-full px-3 py-2 border rounded-md shadow-sm ${
                useParsedQuestions 
                  ? 'border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed' 
                  : 'border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500'
              }`}
              rows={6}
              value={questionsText}
              onChange={(e) => handleFieldChange(setQuestionsText, e.target.value)}
              placeholder={useParsedQuestions ? "Original questions (AI parsing completed)" : "Enter your questions here (use blank line to separate questions)"}
              disabled={useParsedQuestions}
              readOnly={useParsedQuestions}
            />
            <div className="mt-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {!useParsedQuestions ? (
                    <>
                      <p className="text-xs text-gray-500">
                        💡 Tip: Use a blank line to separate questions. You can press Enter within a question to format options.
                      </p>
                      <span className="text-xs text-gray-400">•</span>
                      <p className="text-xs text-gray-500 flex items-center">
                        <Camera className="w-3 h-3 mr-1" />
                        Optionally add images below
                      </p>
                    </>
                  ) : (
                    <div className="flex items-center space-x-1">
                      <Check className="w-3 h-3 text-green-500" />
                      <span className="text-xs text-green-600 font-medium">AI parsing completed ✓</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Show individual question lines with image upload when not parsed */}
            {!useParsedQuestions && questionLines.length > 0 && (
              <div className="mt-4 space-y-3">
                <p className="text-xs font-medium text-gray-700">Add images to questions (optional):</p>
                {questionLines.map((line, index) => (
                  <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-semibold">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-700 mb-1 line-clamp-2">{line}</p>
                      <QuestionImageUpload
                        questionIndex={index}
                        onImageUpload={handleImageUpload}
                        onImageRemove={handleImageRemove}
                        existingImageUrls={questionImages.get(index)?.map(img => img.url) || []}
                        disabled={false}
                        maxImages={5}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Show parsing progress */}
            {parsingState.isParsing && (
              <div className="mt-3 p-3 bg-blue-50 rounded-md">
                <div className="flex items-center space-x-3">
                  <div className="flex-1">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-blue-700">Creating preview...</span>
                      <span className="text-blue-600">{parsingState.progress}%</span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${parsingState.progress}%` }}
                      ></div>
                    </div>
                    {parsingState.currentStep && (
                      <p className="text-xs text-blue-600 mt-1">{parsingState.currentStep}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Inline Parsed Questions Preview */}
            {useParsedQuestions && parsedQuestions.length > 0 && (
              <div className="mt-4">
                <InlineQuestionPreview
                  questions={parsedQuestions}
                  onEdit={handleEditParsedQuestions}
                  onRemoveImage={handleImageRemove}
                  imageUrls={new Map(Array.from(questionImages.entries()).map(([k, v]) => [k, v.map(img => img.url)]))}
                />
              </div>
            )}
            
          </div>
          
          <div className="mb-6">
            <label htmlFor="context" className="block text-sm font-medium text-gray-700 mb-2">
              Context (Optional)
            </label>
            <textarea
              id="context"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm 
                      focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              rows={3}
              value={context}
              onChange={(e) => handleFieldChange(setContext, e.target.value)}
              placeholder="Any additional context about what you're trying to learn"
            />
          </div>
        
      

      
      <div className="flex justify-between items-center">
        <div>
          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}
        </div>
        <Button
          variant="primary"
          size="lg"
          onClick={handleSubmit}
          disabled={!isFormValid || isSubmitting || parsingState.isParsing}
          className="whitespace-nowrap"
        >
          {parsingState.isParsing ? 'Parsing...' : isSubmitting ? 'Starting...' : useParsedQuestions ? 'Start Simulation' : 'Review'}
        </Button>
      </div>
      </>

      )}
    </div>
  );
};

export default SurveyAndFocusGroups;
